import React, { useState } from 'react';
import amazonAPI from '../services/amazonAPI';
import { useSettings } from '../contexts/SettingsContext';
import packageJson from '../../package.json';

// ── Design tokens (matches FocusMode / DesktopDashboard) ──────────────────────
const T = {
  green800:  '#1a4a1c',
  green700:  '#1e5e20',
  green600:  '#2a7a2d',
  green500:  '#3a9a3e',
  green200:  '#b8e0bb',
  green100:  '#dff0e0',
  green50:   '#f0f9f1',
  teal:      '#1ec98a',
  amber:     '#c8761a',
  amberLight:'#fef3e2',
  bg:        '#f5f8f5',
  white:     '#ffffff',
  text:      '#1a2e1b',
  textMed:   '#4a6a4c',
  textLight: '#8aaa8c',
  border:    '#e0ece0',
  shadow:    'rgba(30,80,32,0.10)',
};

const font = "'DM Sans', -apple-system, sans-serif";

const card = {
  background: T.white,
  borderRadius: 14,
  border: `1px solid ${T.border}`,
  boxShadow: `0 1px 4px ${T.shadow}`,
};

const CATEGORY_META = {
  soil_testing:   { icon: '🧪', label: 'Soil Testing',      tileBg: '#f8f0ff', tileBorder: '#d8b8f0' },
  fertilizer:     { icon: '🌿', label: 'Fertilizers',        tileBg: '#f0f9f1', tileBorder: '#b8e0bb' },
  grass_seed:     { icon: '🌱', label: 'Grass Seed',         tileBg: '#f0f9f1', tileBorder: '#b8e0bb' },
  tools:          { icon: '🔧', label: 'Lawn Tools',         tileBg: '#fef9f0', tileBorder: '#f0ddb0' },
  pest_control:   { icon: '🐛', label: 'Pest Control',       tileBg: '#fff0f0', tileBorder: '#f0c0c0' },
  weed_control:   { icon: '🌾', label: 'Weed Control',       tileBg: '#fef3e2', tileBorder: '#f0c080' },
  watering:       { icon: '💧', label: 'Watering Systems',   tileBg: '#e8f4fd', tileBorder: '#b8d8f0' },
  disease_control:{ icon: '🦠', label: 'Disease Control',    tileBg: '#e8f4fd', tileBorder: '#b8d8f0' },
};

// Categories recommended for the current month
const RECOMMENDED_CATS = ['fertilizer', 'grass_seed', 'soil_testing'];

// ── Category card ──────────────────────────────────────────────────────────────
const CategoryCard = ({ category, products, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const meta = CATEGORY_META[category] || { icon: '📦', label: category, tileBg: T.green50, tileBorder: T.green200 };
  const recommended = RECOMMENDED_CATS.includes(category);

  return (
    <button
      onClick={() => onClick(category)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...card,
        position: 'relative',
        border: `1.5px solid ${hovered ? T.green600 : recommended ? '#f0c080' : T.border}`,
        boxShadow: hovered ? `0 4px 16px ${T.shadow}` : `0 1px 4px ${T.shadow}`,
        background: hovered ? T.green50 : T.white,
        padding: '18px 12px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
        fontFamily: font,
        transition: 'all 0.15s',
        width: '100%',
      }}
    >
      {recommended && (
        <div style={{
          position: 'absolute', top: 8, right: 8,
          width: 7, height: 7, borderRadius: 99, background: T.amber,
        }} />
      )}
      <div style={{
        width: 52, height: 52, borderRadius: 14,
        background: meta.tileBg,
        border: `1px solid ${meta.tileBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24,
      }}>
        {meta.icon}
      </div>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: T.text, lineHeight: 1.3 }}>{meta.label}</div>
        <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
          {products.length} product{products.length !== 1 ? 's' : ''}
        </div>
      </div>
    </button>
  );
};

// ── Product card ───────────────────────────────────────────────────────────────
const ProductCard = ({ product }) => {
  const [imageError, setImageError] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState(product.imageUrl);

  const handleImageError = () => {
    if (!imageError) {
      const baseUrl = `/images/products/${product.asin}`;
      const formats = ['.png', '.jpeg', '.webp'];
      const currentFormat = currentImageUrl.split('.').pop();
      const currentIndex = formats.findIndex(f => f === `.${currentFormat}`);
      if (currentIndex < formats.length - 1) {
        setCurrentImageUrl(`${baseUrl}${formats[currentIndex + 1]}`);
      } else {
        setImageError(true);
        setCurrentImageUrl(amazonAPI.getFallbackImageUrl());
      }
    }
  };

  const handleClick = () => {
    amazonAPI.trackAffiliateClick(product.asin, 'affiliate_store', 'store');
    window.open(product.affiliateUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <div style={{
      ...card,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      {/* Image */}
      <div style={{
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
        borderBottom: `1px solid ${T.border}`,
      }}>
        <img
          src={currentImageUrl}
          alt={product.title}
          onError={handleImageError}
          loading="lazy"
          style={{
            width: 100, height: 100,
            objectFit: 'contain',
            borderRadius: 8,
          }}
        />
      </div>

      {/* Info */}
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', flex: 1, gap: 8 }}>
        <div style={{
          fontSize: 12, fontWeight: 600, color: T.text,
          lineHeight: 1.4,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          fontFamily: font,
        }}>
          {product.title}
        </div>

        {/* Rating */}
        {product.rating && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: T.textMed }}>
            <span style={{ color: '#f5a623' }}>★</span>
            <span style={{ fontWeight: 600 }}>{product.rating}</span>
            <span style={{ color: T.textLight }}>({product.reviewCount?.toLocaleString()})</span>
          </div>
        )}

        {/* Price */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16, fontWeight: 800, color: T.text, fontFamily: font }}>
            {product.price}
          </span>
          {product.primeEligible && (
            <span style={{
              fontSize: 10, fontWeight: 700,
              background: '#e8f0fb', color: '#1a4fa8',
              borderRadius: 5, padding: '2px 7px',
            }}>
              Prime
            </span>
          )}
        </div>

        {/* Features */}
        {product.features && product.features.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1 }}>
            {product.features.slice(0, 2).map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontSize: 11, color: T.textMed }}>
                <span style={{ color: T.green600, fontWeight: 700, flexShrink: 0, marginTop: 1 }}>✓</span>
                {f}
              </div>
            ))}
          </div>
        )}

        {/* CTA */}
        <button
          onClick={handleClick}
          style={{
            marginTop: 4,
            background: T.amber,
            color: T.white,
            border: 'none',
            borderRadius: 10,
            padding: '9px 14px',
            fontSize: 12, fontWeight: 700,
            cursor: 'pointer',
            fontFamily: font,
            width: '100%',
            transition: 'opacity 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          View on Amazon
        </button>
      </div>
    </div>
  );
};

// ── AffiliateStore ─────────────────────────────────────────────────────────────
const AffiliateStore = ({ onClose }) => {
  const { settings } = useSettings();
  const [products] = useState(() => ({
    soil_testing: [
      { asin: 'B084TSNR79', title: 'MySoil Soil Test Kit – Complete Nutrient Analysis & Fertilizer Recommendations', price: '$29.95', rating: 4.3, reviewCount: 1847, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B084TSNR79'), affiliateUrl: amazonAPI.generateAffiliateLink('B084TSNR79'), features: ['Professional lab analysis', 'Custom fertilizer recommendations', 'pH and nutrient testing', 'Organic matter analysis'] },
      { asin: 'B08N4QR9C3', title: 'HiHydro 12-Inch Stainless Steel Soil Probe with 2 Sample Bags', price: '$19.99', rating: 4.4, reviewCount: 892, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B08N4QR9C3'), affiliateUrl: amazonAPI.generateAffiliateLink('B08N4QR9C3'), features: ['Stainless steel construction', 'Includes sample bags', 'Professional grade', '12-inch depth'] },
    ],
    tools: [
      { asin: 'B08SMNFTDK', title: 'EarthWay 2600A-PLUS Professional Broadcast Spreader', price: '$89.99', rating: 4.5, reviewCount: 2156, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B08SMNFTDK'), affiliateUrl: amazonAPI.generateAffiliateLink('B08SMNFTDK'), features: ['Professional grade', 'Even distribution', 'Large hopper capacity', 'Pneumatic tires'] },
      { asin: 'B07G2V1DHD', title: 'Corona Tools GT 3234 Reach 7-Tine Extended Rake', price: '$28.99', rating: 4.4, reviewCount: 876, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B07G2V1DHD'), affiliateUrl: amazonAPI.generateAffiliateLink('B07G2V1DHD'), features: ['Extended reach handle', '7-tine design', 'Durable construction', 'Lightweight'] },
      { asin: 'B001D1FFZA', title: "Grampa's Weeder – Original Stand-Up Weed Puller Tool", price: '$32.99', rating: 4.3, reviewCount: 2234, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B001D1FFZA'), affiliateUrl: amazonAPI.generateAffiliateLink('B001D1FFZA'), features: ['No bending required', 'Ergonomic design', 'Long-lasting steel', 'Easy to use'] },
      { asin: 'B0071D0EZK', title: 'Ortho Dial N Spray Hose-End Sprayer', price: '$14.99', rating: 4.3, reviewCount: 2145, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B0071D0EZK'), affiliateUrl: amazonAPI.generateAffiliateLink('B0071D0EZK'), features: ['Dial settings', 'No mixing required', 'Easy attachment', 'Multiple spray patterns'] },
    ],
    fertilizer: [
      { asin: 'B0822WM7YH', title: 'The Andersons Professional PGF Complete 16-4-8 Fertilizer with Humic DG', price: '$45.99', rating: 4.6, reviewCount: 1234, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B0822WM7YH'), affiliateUrl: amazonAPI.generateAffiliateLink('B0822WM7YH'), features: ['Professional grade', 'Slow release', 'Includes humic acid', 'Complete nutrition'] },
      { asin: 'B09B4555L9', title: 'The Andersons Professional PGF 16-0-8 Fertilizer with Humic DG', price: '$42.99', rating: 4.5, reviewCount: 987, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B09B4555L9'), affiliateUrl: amazonAPI.generateAffiliateLink('B09B4555L9'), features: ['No phosphorus formula', 'Humic acid enhanced', 'Slow release nitrogen', 'Professional quality'] },
    ],
    grass_seed: [
      { asin: 'B003AYWIU8', title: 'Jonathan Green Black Beauty Ultra Grass Seed (7 lb)', price: '$39.99', rating: 4.4, reviewCount: 2341, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B003AYWIU8'), affiliateUrl: amazonAPI.generateAffiliateLink('B003AYWIU8'), features: ['Drought tolerant', 'Disease resistant', 'Dense growth', 'Premium seed mix'] },
    ],
    pest_control: [
      { asin: 'B0050DW2M6', title: 'Scotts GrubEx1 Season Long Grub Killer', price: '$24.99', rating: 4.2, reviewCount: 1567, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B0050DW2M6'), affiliateUrl: amazonAPI.generateAffiliateLink('B0050DW2M6'), features: ['Season-long protection', 'Prevents grub damage', 'Easy application', 'Trusted brand'] },
    ],
    weed_control: [
      { asin: 'B000HCRM4S', title: 'Bonide Chickweed, Clover & Oxalis Killer', price: '$16.99', rating: 4.1, reviewCount: 1123, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B000HCRM4S'), affiliateUrl: amazonAPI.generateAffiliateLink('B000HCRM4S'), features: ['Selective herbicide', "Won't harm grass", 'Multiple weed types', 'Ready to use'] },
    ],
    watering: [
      { asin: 'B07CZ864Y9', title: 'Rachio Wi-Fi Smart Sprinkler Controller (8-Zone)', price: '$199.99', rating: 4.6, reviewCount: 3456, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B07CZ864Y9'), affiliateUrl: amazonAPI.generateAffiliateLink('B07CZ864Y9'), features: ['WiFi enabled', 'Weather intelligence', 'Easy scheduling', 'Works with Alexa'] },
    ],
    disease_control: [
      { asin: 'B075R7BNXV', title: 'Scotts DiseaseEx Lawn Fungicide', price: '$19.99', rating: 4.2, reviewCount: 1345, primeEligible: true, imageUrl: amazonAPI.generateImageUrl('B075R7BNXV'), affiliateUrl: amazonAPI.generateAffiliateLink('B075R7BNXV'), features: ['Broad spectrum control', 'Prevents and cures', 'Long-lasting protection', 'Easy application'] },
    ],
  }));

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null); // null = category grid

  const categories = Object.keys(products).sort((a, b) =>
    (CATEGORY_META[a]?.label || a).localeCompare(CATEGORY_META[b]?.label || b)
  );
  const totalProducts = Object.values(products).reduce((s, p) => s + p.length, 0);

  // Filter products when searching
  const allProducts = Object.values(products).flat();
  const filteredProducts = searchTerm
    ? allProducts.filter(p => p.title.toLowerCase().includes(searchTerm.toLowerCase()))
    : selectedCategory
      ? products[selectedCategory]
      : null;

  const activeCategoryLabel = selectedCategory ? CATEGORY_META[selectedCategory]?.label : null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      {/* Backdrop */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)' }} />

      {/* Modal */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: 900,
        height: 'calc(100vh - 48px)',
        maxHeight: 700,
        background: T.bg,
        borderRadius: 20,
        boxShadow: '0 8px 40px rgba(0,0,0,0.22)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: font,
        zIndex: 1,
      }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div style={{
          background: '#1a3d1c',
          padding: '14px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0, gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <img src="/logo192.png" alt="Lawn.Smart logo" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: T.white, lineHeight: 1.2 }}>
                Lawn Care Store
              </div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                {totalProducts} curated products · Zone {settings.hardinessZone}
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.12)', border: 'none', borderRadius: 8,
              width: 30, height: 30, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.8)', fontSize: 16, lineHeight: 1, flexShrink: 0,
            }}
          >
            ×
          </button>
        </div>

        {/* ── Search bar ─────────────────────────────────────────────────── */}
        <div style={{
          padding: '12px 20px',
          background: T.white,
          borderBottom: `1px solid ${T.border}`,
          flexShrink: 0,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {/* Back button */}
          {(selectedCategory && !searchTerm) && (
            <button
              onClick={() => setSelectedCategory(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                background: T.green50, border: `1px solid ${T.green200}`,
                borderRadius: 9, padding: '7px 12px',
                fontSize: 12, fontWeight: 600, color: T.green700,
                cursor: 'pointer', fontFamily: font, flexShrink: 0,
              }}
            >
              ← Back
            </button>
          )}

          <div style={{ position: 'relative', flex: 1 }}>
            <svg style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="15" height="15" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="8" stroke={T.textLight} strokeWidth="2"/>
              <path d="M21 21l-4.35-4.35" stroke={T.textLight} strokeWidth="2" strokeLinecap="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search products…"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); if (e.target.value) setSelectedCategory(null); }}
              style={{
                width: '100%', padding: '9px 14px 9px 36px',
                borderRadius: 10, fontFamily: font,
                border: `1px solid ${T.border}`,
                fontSize: 13, color: T.text,
                background: T.bg, outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Category breadcrumb label */}
          {activeCategoryLabel && !searchTerm && (
            <div style={{
              fontSize: 12, fontWeight: 700, color: T.green700,
              background: T.green50, border: `1px solid ${T.green200}`,
              borderRadius: 8, padding: '5px 11px', flexShrink: 0,
            }}>
              {CATEGORY_META[selectedCategory]?.icon} {activeCategoryLabel}
            </div>
          )}
        </div>

        {/* ── Recommended banner ─────────────────────────────────────────── */}
        {!searchTerm && !selectedCategory && (
          <div style={{
            margin: '12px 20px 0', background: T.amberLight, border: '1px solid #f0c080',
            borderRadius: 12, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
          }}>
            <span style={{ fontSize: 14 }}>⭐</span>
            <div style={{ fontSize: 12, color: '#8a5a1a', lineHeight: 1.4 }}>
              <span style={{ fontWeight: 700, color: T.amber }}>
                Recommended for {new Date().toLocaleString('default', { month: 'long' })} —{' '}
              </span>
              Fertilizers, Grass Seed &amp; Soil Testing match your active tasks
            </div>
          </div>
        )}

        {/* ── Content ────────────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>

          {/* Search results */}
          {searchTerm && (
            <>
              <div style={{ fontSize: 12, color: T.textLight, marginBottom: 14 }}>
                {filteredProducts.length} result{filteredProducts.length !== 1 ? 's' : ''} for "{searchTerm}"
              </div>
              {filteredProducts.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                  {filteredProducts.map(p => <ProductCard key={p.asin} product={p} />)}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🔍</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.textMed }}>No products found</div>
                  <div style={{ fontSize: 12, color: T.textLight, marginTop: 4 }}>Try a different search term</div>
                </div>
              )}
            </>
          )}

          {/* Category grid */}
          {!searchTerm && !selectedCategory && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {categories.map(cat => (
                <CategoryCard
                  key={cat}
                  category={cat}
                  products={products[cat]}
                  onClick={setSelectedCategory}
                />
              ))}
            </div>
          )}

          {/* Product grid for selected category */}
          {!searchTerm && selectedCategory && (
            <>
              {filteredProducts && filteredProducts.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>
                  {filteredProducts.map(p => <ProductCard key={p.asin} product={p} />)}
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '48px 0' }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>📦</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: T.textMed }}>No products in this category</div>
                  <div style={{ fontSize: 12, color: T.textLight, marginTop: 4 }}>Check back later for new products</div>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div style={{
          borderTop: `1px solid ${T.border}`,
          background: T.white,
          padding: '10px 20px',
          flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontFamily: font }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: T.text, lineHeight: 1.2 }}>
              Lawn<span style={{ color: T.teal }}>.Smart</span>
              <span style={{ fontWeight: 400, color: T.textLight, marginLeft: 5 }}>v{packageJson.version}</span>
            </div>
            <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>
              Amazon Associate · Commissions earned at no extra cost to you
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: T.white, color: T.text,
              border: `1.5px solid ${T.border}`,
              borderRadius: 10, padding: '7px 20px',
              fontSize: 13, fontWeight: 600,
              cursor: 'pointer', fontFamily: font, flexShrink: 0,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AffiliateStore;
